class SafioliTheme {
  constructor() {
    this.drawer = document.querySelector('[data-mobile-drawer]');
    this.scrim = document.querySelector('[data-drawer-scrim]');
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.cartScrim = document.querySelector('[data-cart-scrim]');
    this.searchDrawer = document.querySelector('[data-search-drawer]');
    this.searchScrim = document.querySelector('[data-search-scrim]');
    this.lastFocusedElement = null;
    this.cartLastFocusedElement = null;
    this.searchLastFocusedElement = null;
    this.searchAbortController = null;
    this.searchDebounceTimer = null;
    this.bindDrawer();
    this.bindFooterAccordions();
    this.bindNavigationDropdowns();
    this.bindCollectionColourFilter();
    this.bindCollectionFilterDropdowns();
    this.bindCartDrawer();
    this.bindSearchDrawer();
    this.bindSearchKeyboardSubmission();
    this.bindGallery();
    this.bindQuantity();
    this.bindQuantityOffer();
    this.bindVariants();
    this.bindMeasurements();
    this.bindProductTabs();
    this.bindProductDescriptionReadMore();
    this.bindStickyProductBar();
  }

  bindFooterAccordions() {
    const items = [...document.querySelectorAll('[data-footer-accordion]')];
    if (!items.length) return;
    const mobile = window.matchMedia('(max-width: 640px)');

    const sync = (isMobile) => {
      items.forEach((item) => {
        item.open = !isMobile;
        const summary = item.querySelector('summary');
        if (summary) summary.tabIndex = isMobile ? 0 : -1;
      });
    };

    sync(mobile.matches);
    mobile.addEventListener?.('change', (event) => sync(event.matches));
  }

  bindNavigationDropdowns() {
    const groups = [...document.querySelectorAll('.desktop-nav [data-nav-dropdown]')];
    if (!groups.length) return;
    const closeTimers = new WeakMap();

    const closeOthers = (current) => {
      groups.forEach((group) => {
        if (group !== current) group.open = false;
      });
    };

    groups.forEach((group) => {
      group.addEventListener('toggle', () => {
        if (group.open) closeOthers(group);
      });

      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        group.addEventListener('mouseenter', () => {
          window.clearTimeout(closeTimers.get(group));
          closeOthers(group);
          group.open = true;
        });
        group.addEventListener('mouseleave', () => {
          closeTimers.set(group, window.setTimeout(() => {
            group.open = false;
          }, 260));
        });
      }
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('.desktop-nav [data-nav-dropdown]')) {
        closeOthers(null);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openGroup = groups.find((group) => group.open);
      if (!openGroup) return;
      openGroup.open = false;
      openGroup.querySelector('summary')?.focus();
    });
  }

  bindCollectionColourFilter() {
    const filter = document.querySelector('[data-manual-colour-filter]');
    if (!filter) return;

    const cards = [...document.querySelectorAll('[data-product-colours]')];
    const choices = [...filter.querySelectorAll('[data-colour-choice]')];
    const inputs = [...filter.querySelectorAll('[data-colour-input]')];
    const reset = filter.querySelector('[data-colour-reset]');
    const selectedCount = filter.querySelector('[data-colour-selected-count]');
    const queryInput = filter.querySelector('[data-colour-query-input]');
    const emptyState = document.querySelector('[data-colour-empty]');
    const pagination = document.querySelector('.collection-body .pagination');

    const normalise = (value = '') => value
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9\u0600-\u06ff]+/g, '-')
      .replace(/^-|-$/g, '');

    const aliases = {
      white: 'blanc',
      cream: 'creme',
      sand: 'sable',
      grey: 'gris',
      gray: 'gris',
      'navy-blue': 'bleu-marine',
      navy: 'bleu-marine',
      'petrol-blue': 'bleu-petrole',
      teal: 'bleu-petrole',
      'mint-green': 'vert-menthe',
      'olive-green': 'vert-olive',
      burgundy: 'bordeaux',
      lilac: 'lilas',
      'cobalt-blue': 'bleu-cobalt',
      yellow: 'jaune',
      'ابيض': 'blanc',
      'كريمي': 'creme',
      'رملي': 'sable',
      'رمادي': 'gris',
      'اسود': 'noir',
      'ازرق-داكن': 'bleu-marine',
      'ازرق-بترولي': 'bleu-petrole',
      'اخضر-نعناعي': 'vert-menthe',
      'اخضر-زيتوني': 'vert-olive',
      'خمري': 'bordeaux',
      'ليلكي': 'lilas',
      'ازرق-كوبالت': 'bleu-cobalt',
      'اصفر': 'jaune'
    };
    const canonical = (value) => aliases[normalise(value)] || normalise(value);

    const coloursByCard = new Map(cards.map((card) => {
      const colours = (card.dataset.productColours || '')
        .split('||')
        .map(canonical)
        .filter(Boolean);
      return [card, new Set(colours)];
    }));

    const colourImagesByCard = new Map(cards.map((card) => {
      const image = card.querySelector('.product-card__image');
      const links = [...card.querySelectorAll('[data-product-card-link]')];
      const variants = new Map();

      card.querySelectorAll('[data-card-colour-image]').forEach((entry) => {
        const colour = canonical(entry.dataset.colourValue || '');
        if (!colour || variants.has(colour)) return;
        variants.set(colour, {
          src: entry.dataset.imageSrc || '',
          srcset: entry.dataset.imageSrcset || '',
          alt: entry.dataset.imageAlt || '',
          url: entry.dataset.variantUrl || ''
        });
      });

      return [card, {
        image,
        links,
        variants,
        original: {
          src: image?.getAttribute('src') || '',
          srcset: image?.getAttribute('srcset') || '',
          alt: image?.getAttribute('alt') || '',
          url: links[0]?.getAttribute('href') || ''
        }
      }];
    }));

    const updateCardImage = (card, selectedColours) => {
      const data = colourImagesByCard.get(card);
      if (!data?.image) return;

      const matchedColour = selectedColours.find((colour) => data.variants.has(colour));
      const next = matchedColour ? data.variants.get(matchedColour) : data.original;
      const activeColour = matchedColour || '';
      if (data.image.dataset.activeColour === activeColour) return;

      if (next.src) data.image.setAttribute('src', next.src);
      if (next.srcset) data.image.setAttribute('srcset', next.srcset);
      else data.image.removeAttribute('srcset');
      data.image.setAttribute('alt', next.alt || data.original.alt);
      if (next.url) data.links.forEach((link) => link.setAttribute('href', next.url));
      data.image.dataset.activeColour = activeColour;
    };

    const counts = new Map();
    coloursByCard.forEach((colours) => colours.forEach((colour) => {
      counts.set(colour, (counts.get(colour) || 0) + 1);
    }));

    let availableColourCount = 0;
    choices.forEach((choice) => {
      const key = choice.dataset.colourKey;
      const count = counts.get(key) || 0;
      if (count > 0) availableColourCount += 1;
      choice.hidden = count === 0;
      const input = choice.querySelector('[data-colour-input]');
      const countNode = choice.querySelector('[data-colour-count]');
      if (input) input.disabled = count === 0;
      if (countNode) countNode.textContent = String(count);
    });
    filter.hidden = availableColourCount === 0;

    const updateUrl = (selected) => {
      const url = new URL(window.location.href);
      if (selected.size) url.searchParams.set('safioli_colour', [...selected].join(','));
      else url.searchParams.delete('safioli_colour');
      window.history.replaceState({}, '', url);
    };

    const apply = ({ updateHistory = true } = {}) => {
      const selectedColours = inputs.filter((input) => input.checked && !input.disabled).map((input) => input.value);
      const selected = new Set(selectedColours);
      let visibleCount = 0;

      coloursByCard.forEach((colours, card) => {
        const matches = selected.size === 0 || [...selected].some((colour) => colours.has(colour));
        card.hidden = !matches;
        updateCardImage(card, selectedColours);
        if (matches) visibleCount += 1;
      });

      if (selectedCount) {
        selectedCount.textContent = String(selected.size);
        selectedCount.hidden = selected.size === 0;
      }
      if (queryInput) {
        queryInput.value = [...selected].join(',');
        queryInput.disabled = selected.size === 0;
      }
      if (emptyState) emptyState.hidden = visibleCount > 0;
      if (pagination) pagination.hidden = selected.size > 0;
      if (updateHistory) updateUrl(selected);
    };

    const requestedColour = (new URL(window.location.href).searchParams.get('safioli_colour') || '').split(',').find(Boolean) || '';
    inputs.forEach((input) => { input.checked = input.value === requestedColour && !input.disabled; });
    inputs.forEach((input) => input.addEventListener('change', () => {
      if (input.checked) {
        inputs.forEach((otherInput) => {
          if (otherInput !== input) otherInput.checked = false;
        });
      }
      apply();
      filter.open = false;
    }));
    reset?.addEventListener('click', () => {
      inputs.forEach((input) => { input.checked = false; });
      apply();
    });
    apply({ updateHistory: false });
  }

  bindCollectionFilterDropdowns() {
    const toolbar = document.querySelector('.collection-toolbar');
    if (!toolbar) return;
    const dropdowns = [...toolbar.querySelectorAll('.filter-dropdown')];
    if (!dropdowns.length) return;

    dropdowns.forEach((dropdown) => {
      dropdown.addEventListener('toggle', () => {
        if (!dropdown.open) return;
        dropdowns.forEach((other) => {
          if (other !== dropdown) other.open = false;
        });
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openDropdown = dropdowns.find((dropdown) => dropdown.open);
      if (!openDropdown) return;
      openDropdown.open = false;
      openDropdown.querySelector('summary')?.focus();
    });
  }

  bindDrawer() {
    const open = document.querySelector('[data-menu-open]');
    const close = document.querySelector('[data-menu-close]');
    if (!this.drawer || !open || !close || !this.scrim) return;
    const setState = (active) => {
      if (active) {
        this.lastFocusedElement = document.activeElement;
        this.drawer.hidden = false;
      }
      this.drawer.classList.toggle('is-open', active);
      this.drawer.setAttribute('aria-hidden', String(!active));
      this.drawer.inert = !active;
      this.scrim.hidden = !active;
      document.body.classList.toggle('drawer-open', active);
      open.setAttribute('aria-expanded', String(active));
      if (active) close.focus();
      else {
        this.drawer.hidden = true;
        if (this.lastFocusedElement instanceof HTMLElement) this.lastFocusedElement.focus();
      }
    };
    open.addEventListener('click', () => setState(true));
    close.addEventListener('click', () => setState(false));
    this.scrim.addEventListener('click', () => setState(false));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) setState(false);
      if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        const focusable = [...this.drawer.querySelectorAll('a[href],button:not([disabled]),summary,select:not([disabled]),input:not([disabled])')];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
  }

  bindCartDrawer() {
    if (!this.cartDrawer || !this.cartScrim) return;

    const setState = (active) => {
      if (active) {
        this.cartLastFocusedElement = document.activeElement;
        this.cartDrawer.hidden = false;
        this.setSearchDrawerState(false, false);
        if (this.drawer?.classList.contains('is-open')) {
          this.drawer.classList.remove('is-open');
          this.drawer.setAttribute('aria-hidden', 'true');
          this.drawer.inert = true;
          this.drawer.hidden = true;
          this.scrim.hidden = true;
          document.querySelector('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
          document.body.classList.remove('drawer-open');
        }
      }
      this.cartDrawer.classList.toggle('is-open', active);
      this.cartDrawer.setAttribute('aria-hidden', String(!active));
      this.cartDrawer.inert = !active;
      this.cartScrim.hidden = !active;
      document.body.classList.toggle('cart-drawer-open', active);
      document.querySelectorAll('[data-cart-open]').forEach((opener) => opener.setAttribute('aria-expanded', String(active)));
      if (active) this.cartDrawer.querySelector('[data-cart-close]')?.focus();
      else {
        this.cartDrawer.hidden = true;
        if (this.cartLastFocusedElement instanceof HTMLElement) this.cartLastFocusedElement.focus();
      }
    };

    document.addEventListener('click', async (event) => {
      if (!(event.target instanceof Element)) return;

      const opener = event.target.closest('[data-cart-open]');
      if (opener) {
        event.preventDefault();
        setState(true);
        return;
      }

      if (event.target.closest('[data-cart-close]')) {
        event.preventDefault();
        setState(false);
        return;
      }

      const changeButton = event.target.closest('[data-cart-change]');
      if (!changeButton) return;
      event.preventDefault();
      const key = changeButton.dataset.cartKey;
      const quantity = Number(changeButton.dataset.cartQuantity);
      if (!key || Number.isNaN(quantity)) return;
      await this.changeCartLine(key, quantity);
    });

    this.cartScrim.addEventListener('click', () => setState(false));

    document.addEventListener('keydown', (event) => {
      if (!this.cartDrawer.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        setState(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.cartDrawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('submit', async (event) => {
      if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-product-form]')) return;
      event.preventDefault();
      const form = event.target;
      const submitButton = form.querySelector('[data-main-add]');
      if (submitButton?.disabled) return;
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.textContent = submitButton.dataset.addingLabel || submitButton.dataset.addLabel;
      }
      this.cartDrawer.classList.add('is-loading');
      this.cartDrawer.setAttribute('aria-busy', 'true');
      let itemAdded = false;

      try {
        const response = await fetch(this.cartDrawer.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error('Unable to add item');
        itemAdded = true;
        await this.refreshCartDrawer();
        setState(true);
      } catch (error) {
        if (!itemAdded) HTMLFormElement.prototype.submit.call(form);
        else window.location.assign(this.cartDrawer.dataset.cartUrl);
      } finally {
        this.cartDrawer.classList.remove('is-loading');
        this.cartDrawer.removeAttribute('aria-busy');
        if (submitButton) {
          const select = form.querySelector('[data-variant-select]');
          const available = select ? select.options[select.selectedIndex]?.dataset.available === 'true' : true;
          submitButton.disabled = !available;
          submitButton.removeAttribute('aria-busy');
          submitButton.textContent = available ? submitButton.dataset.addLabel : submitButton.dataset.soldLabel;
        }
      }
    });
  }

  async changeCartLine(key, quantity) {
    this.cartDrawer.classList.add('is-loading');
    this.cartDrawer.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.cartDrawer.dataset.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error('Unable to update cart');
      await this.refreshCartDrawer();
    } catch (error) {
      window.location.assign(this.cartDrawer.dataset.cartUrl);
    } finally {
      this.cartDrawer.classList.remove('is-loading');
      this.cartDrawer.removeAttribute('aria-busy');
    }
  }

  async refreshCartDrawer() {
    const rootUrl = new URL(this.cartDrawer.dataset.cartRootUrl || '/', window.location.origin);
    rootUrl.searchParams.set('section_id', this.cartDrawer.dataset.cartSectionId);
    const response = await fetch(`${rootUrl.pathname}${rootUrl.search}`, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Unable to refresh cart');
    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const incomingDrawer = documentFragment.querySelector('[data-cart-drawer]');
    const incomingContent = incomingDrawer?.querySelector('[data-cart-drawer-content]');
    const currentContent = this.cartDrawer.querySelector('[data-cart-drawer-content]');
    if (!incomingDrawer || !incomingContent || !currentContent) throw new Error('Invalid cart response');

    currentContent.innerHTML = incomingContent.innerHTML;
    const itemCount = Number(incomingDrawer.dataset.cartCountValue || 0);
    this.cartDrawer.dataset.cartCountValue = String(itemCount);
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(itemCount);
      count.classList.toggle('is-hidden', itemCount === 0);
    });
    const liveRegion = this.cartDrawer.querySelector('[data-cart-live]');
    if (liveRegion) {
      const label = this.cartDrawer.dataset.cartUpdatedLabel || '';
      liveRegion.textContent = label.replace('[count]', String(itemCount));
    }
  }

  setSearchDrawerState(active, restoreFocus = true) {
    if (!this.searchDrawer || !this.searchScrim) return;
    const wasOpen = this.searchDrawer.classList.contains('is-open');

    if (active) {
      this.searchLastFocusedElement = document.activeElement;
      this.searchDrawer.hidden = false;

      if (this.drawer?.classList.contains('is-open')) {
        this.drawer.classList.remove('is-open');
        this.drawer.setAttribute('aria-hidden', 'true');
        this.drawer.inert = true;
        this.drawer.hidden = true;
        if (this.scrim) this.scrim.hidden = true;
        document.querySelector('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('drawer-open');
      }

      if (this.cartDrawer?.classList.contains('is-open')) {
        this.cartDrawer.classList.remove('is-open');
        this.cartDrawer.setAttribute('aria-hidden', 'true');
        this.cartDrawer.inert = true;
        this.cartDrawer.hidden = true;
        if (this.cartScrim) this.cartScrim.hidden = true;
        document.querySelectorAll('[data-cart-open]').forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
        document.body.classList.remove('cart-drawer-open');
      }
    }

    this.searchDrawer.classList.toggle('is-open', active);
    this.searchDrawer.setAttribute('aria-hidden', String(!active));
    this.searchDrawer.inert = !active;
    this.searchScrim.hidden = !active;
    document.body.classList.toggle('search-drawer-open', active);
    document.querySelectorAll('[data-search-open]').forEach((opener) => opener.setAttribute('aria-expanded', String(active)));

    if (active) {
      window.setTimeout(() => this.searchDrawer.querySelector('[data-search-input]')?.focus(), 40);
    } else {
      this.searchDrawer.hidden = true;
      window.clearTimeout(this.searchDebounceTimer);
      this.searchAbortController?.abort();
      this.searchDrawer.classList.remove('is-loading');
      this.searchDrawer.removeAttribute('aria-busy');
      if (wasOpen && restoreFocus && this.searchLastFocusedElement instanceof HTMLElement) this.searchLastFocusedElement.focus();
    }
  }

  bindSearchDrawer() {
    if (!this.searchDrawer || !this.searchScrim) return;
    const form = this.searchDrawer.querySelector('[data-search-form]');
    const input = this.searchDrawer.querySelector('[data-search-input]');
    if (!form || !input) return;

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-search-open]')) {
        event.preventDefault();
        this.setSearchDrawerState(true);
      } else if (event.target.closest('[data-search-close]')) {
        event.preventDefault();
        this.setSearchDrawerState(false);
      }
    });

    this.searchScrim.addEventListener('click', () => this.setSearchDrawerState(false));

    form.addEventListener('submit', (event) => {
      const query = input.value.trim();
      if (query.length < 2) {
        event.preventDefault();
        input.focus();
        return;
      }
      window.clearTimeout(this.searchDebounceTimer);
      this.searchAbortController?.abort();
      input.value = query;
    });

    input.addEventListener('input', () => {
      const query = input.value.trim();
      window.clearTimeout(this.searchDebounceTimer);
      this.searchAbortController?.abort();

      if (query.length < 2) {
        this.searchDrawer.classList.remove('is-loading');
        this.searchDrawer.removeAttribute('aria-busy');
        this.renderSearchMessage(this.searchDrawer.dataset.searchStartText || input.placeholder);
        return;
      }

      this.searchDebounceTimer = window.setTimeout(() => this.performPredictiveSearch(query), 250);
    });

    document.addEventListener('keydown', (event) => {
      if (!this.searchDrawer.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        this.setSearchDrawerState(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.searchDrawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  bindSearchKeyboardSubmission() {
    document.querySelectorAll('[data-search-keyboard-form]').forEach((form) => {
      const input = form.querySelector('input[type="search"][name="q"]');
      if (!input) return;
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        event.preventDefault();
        if (input.value.trim().length < 2) {
          input.focus();
          return;
        }
        form.requestSubmit();
      });
    });
  }

  async performPredictiveSearch(query) {
    const results = this.searchDrawer.querySelector('[data-search-results]');
    const status = this.searchDrawer.querySelector('[data-search-status]');
    if (!results || !status) return;

    this.searchAbortController?.abort();
    const controller = new AbortController();
    this.searchAbortController = controller;
    this.searchDrawer.classList.add('is-loading');
    this.searchDrawer.setAttribute('aria-busy', 'true');
    status.textContent = this.searchDrawer.dataset.searchLoadingText;

    try {
      const predictiveUrl = this.searchDrawer.dataset.predictiveSearchUrl || '/search/suggest';
      const endpoint = new URL(predictiveUrl.endsWith('.json') ? predictiveUrl : `${predictiveUrl}.json`, window.location.origin);
      endpoint.searchParams.set('q', query);
      endpoint.searchParams.set('resources[type]', 'product');
      endpoint.searchParams.set('resources[limit]', '8');
      endpoint.searchParams.set('resources[options][unavailable_products]', 'last');

      const response = await fetch(endpoint.toString(), {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to search');
      const data = await response.json();
      const products = data.resources?.results?.products || [];
      this.renderSearchResults(products, query);
    } catch (error) {
      if (error.name !== 'AbortError') this.renderSearchMessage(this.searchDrawer.dataset.searchNoResultsText);
    } finally {
      if (!controller.signal.aborted) {
        this.searchDrawer.classList.remove('is-loading');
        this.searchDrawer.removeAttribute('aria-busy');
      }
    }
  }

  renderSearchMessage(message) {
    const results = this.searchDrawer.querySelector('[data-search-results]');
    const status = this.searchDrawer.querySelector('[data-search-status]');
    if (!results || !status) return;
    const hint = document.createElement('div');
    const copy = document.createElement('p');
    hint.className = 'search-drawer__hint';
    copy.textContent = message;
    hint.append(copy);
    results.replaceChildren(hint);
    status.textContent = message;
  }

  renderSearchResults(products, query) {
    const results = this.searchDrawer.querySelector('[data-search-results]');
    const status = this.searchDrawer.querySelector('[data-search-status]');
    if (!results || !status) return;

    if (!products.length) {
      this.renderSearchMessage(this.searchDrawer.dataset.searchNoResultsText);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'search-results-grid';

    products.forEach((product) => {
      const card = document.createElement('article');
      const mediaLink = document.createElement('a');
      const titleLink = document.createElement('a');
      const price = document.createElement('p');
      card.className = 'search-result-card';
      mediaLink.className = 'search-result-card__media';
      mediaLink.href = product.url;
      titleLink.className = 'search-result-card__title';
      titleLink.href = product.url;
      titleLink.textContent = product.title;
      price.className = 'search-result-card__price';
      price.textContent = this.formatSearchPrice(product.price_min ?? product.price);

      if (product.featured_image?.url) {
        const image = document.createElement('img');
        image.src = product.featured_image.url;
        image.alt = product.featured_image.alt || product.title;
        image.loading = 'lazy';
        image.width = product.featured_image.width || 600;
        image.height = product.featured_image.height || 600;
        mediaLink.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'search-result-card__placeholder';
        placeholder.textContent = 'SAFIOLI';
        mediaLink.append(placeholder);
      }

      if (product.available === false) {
        const badge = document.createElement('span');
        badge.className = 'search-result-card__badge';
        badge.textContent = this.searchDrawer.dataset.searchSoldOutText;
        mediaLink.append(badge);
      }

      card.append(mediaLink, titleLink, price);
      grid.append(card);
    });

    const actions = document.createElement('div');
    const viewAll = document.createElement('a');
    const searchUrl = this.searchDrawer.dataset.searchUrl || '/search';
    actions.className = 'search-drawer__view-all';
    viewAll.className = 'text-link';
    viewAll.href = `${searchUrl}?q=${encodeURIComponent(query)}&type=product`;
    viewAll.textContent = this.searchDrawer.dataset.searchViewAllText;
    actions.append(viewAll);
    results.replaceChildren(grid, actions);
    status.textContent = `${products.length} ${this.searchDrawer.dataset.searchResultsText}`;
  }

  formatSearchPrice(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return value == null ? '' : String(value);
    const currency = this.searchDrawer.dataset.searchCurrency || 'MAD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'MAD' ? 0 : 2,
      }).format(amount);
    } catch (error) {
      return `${amount} ${currency}`;
    }
  }

  bindGallery() {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
      const lightbox = gallery.querySelector('[data-gallery-lightbox]');
      const lightboxImage = lightbox?.querySelector('[data-lightbox-image]');
      const lightboxViewport = lightbox?.querySelector('[data-lightbox-viewport]');
      const lightboxCurrent = lightbox?.querySelector('[data-lightbox-current]');
      const lightboxClose = lightbox?.querySelector('[data-lightbox-close]');
      const lightboxPrevious = lightbox?.querySelector('[data-lightbox-previous]');
      const lightboxNext = lightbox?.querySelector('[data-lightbox-next]');
      if (!slides.length) return;

      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let lightboxLastFocus = null;

      const syncLightbox = () => {
        if (!lightbox || !lightboxImage) return;
        const activeSlide = slides[activeIndex];
        const sourceImage = activeSlide.querySelector('img');
        lightbox.classList.remove('is-zoomed');
        lightboxViewport?.scrollTo({ top: 0, left: 0 });
        lightboxImage.src = activeSlide.dataset.galleryZoomSrc || sourceImage?.currentSrc || sourceImage?.src || '';
        lightboxImage.alt = sourceImage?.alt || '';
        if (lightboxCurrent) lightboxCurrent.textContent = String(activeIndex + 1);
      };

      const updateState = (index, revealThumbnail = false) => {
        activeIndex = Math.max(0, Math.min(index, slides.length - 1));
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === activeIndex));
        thumbs.forEach((thumb, thumbIndex) => {
          const isActive = thumbIndex === activeIndex;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-pressed', String(isActive));
        });
        if (revealThumbnail) thumbs[activeIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        if (lightbox && !lightbox.hidden) syncLightbox();
      };

      const setLightboxState = (active) => {
        if (!lightbox || !lightboxImage) return;
        if (active) {
          lightboxLastFocus = document.activeElement;
          syncLightbox();
          lightbox.hidden = false;
          lightbox.setAttribute('aria-hidden', 'false');
          document.body.classList.add('product-lightbox-open');
          window.setTimeout(() => lightboxClose?.focus(), 20);
        } else {
          lightbox.hidden = true;
          lightbox.setAttribute('aria-hidden', 'true');
          lightbox.classList.remove('is-zoomed');
          document.body.classList.remove('product-lightbox-open');
          if (lightboxLastFocus instanceof HTMLElement) lightboxLastFocus.focus();
        }
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => updateState(Number(thumb.dataset.galleryIndex || 0)));
      });

      gallery.addEventListener('safioli:select-media', (event) => {
        const mediaId = String(event.detail?.mediaId || '');
        if (!mediaId) return;
        const mediaIndex = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (mediaIndex >= 0) updateState(mediaIndex, true);
      });

      slides.forEach((slide, index) => {
        slide.addEventListener('pointermove', (event) => {
          if (event.pointerType && event.pointerType !== 'mouse') return;
          const bounds = slide.getBoundingClientRect();
          const x = ((event.clientX - bounds.left) / bounds.width) * 100;
          const y = ((event.clientY - bounds.top) / bounds.height) * 100;
          slide.style.setProperty('--gallery-zoom-x', `${Math.max(0, Math.min(x, 100))}%`);
          slide.style.setProperty('--gallery-zoom-y', `${Math.max(0, Math.min(y, 100))}%`);
        });
        slide.addEventListener('click', () => {
          updateState(index);
          setLightboxState(true);
        });
      });

      if (slides.length < 2) {
        if (lightboxPrevious) lightboxPrevious.hidden = true;
        if (lightboxNext) lightboxNext.hidden = true;
      }

      lightboxClose?.addEventListener('click', () => setLightboxState(false));
      lightboxPrevious?.addEventListener('click', () => updateState((activeIndex - 1 + slides.length) % slides.length, true));
      lightboxNext?.addEventListener('click', () => updateState((activeIndex + 1) % slides.length, true));
      lightboxImage?.addEventListener('click', () => {
        lightbox.classList.toggle('is-zoomed');
        if (!lightbox.classList.contains('is-zoomed')) lightboxViewport?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      });
      lightbox?.addEventListener('click', (event) => {
        if (event.target === lightbox) setLightboxState(false);
      });

      document.addEventListener('keydown', (event) => {
        if (!lightbox || lightbox.hidden) return;
        if (event.key === 'Escape') {
          setLightboxState(false);
          return;
        }
        if (event.key === 'ArrowLeft') updateState((activeIndex - 1 + slides.length) % slides.length, true);
        if (event.key === 'ArrowRight') updateState((activeIndex + 1) % slides.length, true);
        if (event.key !== 'Tab') return;
        const focusable = [...lightbox.querySelectorAll('button:not([disabled]):not([hidden])')];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      updateState(activeIndex);
    });
  }

  bindQuantity() {
    document.querySelectorAll('[data-quantity]').forEach((control) => {
      const input = control.querySelector('input');
      control.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.dataset.step || 0);
          const min = Number(input.min || 1);
          input.value = Math.max(min, Number(input.value || min) + step);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  bindQuantityOffer() {
    document.querySelectorAll('[data-quantity-offer]').forEach((offer) => {
      const form = offer.closest('[data-product-form]');
      const quantityInput = form?.querySelector('input[name="quantity"]');
      const variantSelect = form?.querySelector('[data-variant-select]');
      const choices = [...offer.querySelectorAll('[data-offer-choice]')];
      const minimum = Number(offer.dataset.discountQuantity || 2);
      const percentage = Number(offer.dataset.discountPercentage || 10);
      if (!quantityInput || !choices.length) return;

      const formatMoney = (cents) => {
        const amount = Number(cents) / 100;
        const currency = offer.dataset.currencyCode || 'MAD';
        if (currency === 'MAD') return `${amount.toFixed(2)} dh`;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(amount);
        } catch (error) {
          return `${amount.toFixed(2)} ${currency}`;
        }
      };

      const syncOffer = () => {
        const quantity = Number(quantityInput.value || 1);
        choices.forEach((choice) => {
          const choiceQuantity = Number(choice.dataset.offerQuantity);
          const active = choiceQuantity === 1 ? quantity === 1 : quantity >= minimum;
          choice.classList.toggle('is-active', active);
          choice.setAttribute('aria-checked', String(active));
        });
        const option = variantSelect?.options[variantSelect.selectedIndex];
        const basePriceCents = Number(option?.dataset.priceCents || offer.dataset.basePriceCents || 0);
        if (!basePriceCents) return;
        offer.dataset.basePriceCents = String(basePriceCents);
        const displayedQuantity = quantity >= minimum ? quantity : minimum;
        const originalCents = basePriceCents * displayedQuantity;
        const totalCents = Math.round(originalCents * (100 - percentage) / 100);
        const savingCents = originalCents - totalCents;
        const single = offer.querySelector('[data-offer-single]');
        const total = offer.querySelector('[data-offer-total]');
        const original = offer.querySelector('[data-offer-original]');
        const saving = offer.querySelector('[data-offer-saving-label]');
        const bundleLabel = offer.querySelector('[data-offer-bundle-label]');
        if (single) single.textContent = formatMoney(basePriceCents);
        if (total) total.textContent = formatMoney(totalCents);
        if (original) original.textContent = formatMoney(originalCents);
        if (saving) saving.textContent = saving.dataset.offerSavingLabel.replace('__AMOUNT__', formatMoney(savingCents));
        if (bundleLabel) bundleLabel.textContent = bundleLabel.dataset.offerBundleTemplate.replace('__COUNT__', String(displayedQuantity));
      };

      choices.forEach((choice) => {
        choice.addEventListener('click', () => {
          quantityInput.value = choice.dataset.offerQuantity || '1';
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          syncOffer();
        });
      });
      quantityInput.addEventListener('input', syncOffer);
      quantityInput.addEventListener('change', syncOffer);
      variantSelect?.addEventListener('change', syncOffer);
      syncOffer();
    });
  }

  bindVariants() {
    const select = document.querySelector('[data-variant-select]');
    const price = document.querySelector('[data-product-price]');
    const availability = document.querySelector('[data-product-availability]');
    const deliveryEstimate = document.querySelector('[data-product-delivery-estimate]');
    const mainButton = document.querySelector('[data-main-add]');
    const stickyButton = document.querySelector('[data-sticky-add]');
    const whatsappOrder = document.querySelector('[data-whatsapp-order]');
    const swatches = [...document.querySelectorAll('[data-variant-swatch]')];
    const selectedVariantLabel = document.querySelector('[data-selected-variant-label]');
    const quantityInput = document.querySelector('[data-product-form] input[name="quantity"]');
    const gallery = document.querySelector('[data-product-gallery]');

    const updateWhatsAppOrder = () => {
      if (!whatsappOrder) return;
      const option = select?.options[select.selectedIndex];
      const variantLabel = option?.dataset.label || option?.textContent.split('—')[0].trim() || '';
      const quantity = Math.max(1, Number(quantityInput?.value || 1));
      const productUrl = new URL(whatsappOrder.dataset.whatsappUrl || window.location.href, window.location.origin);
      if (option?.value) productUrl.searchParams.set('variant', option.value);
      const messageParts = [
        `${whatsappOrder.dataset.whatsappIntro || ''} ${whatsappOrder.dataset.whatsappProduct || ''}`.trim(),
        variantLabel ? `${whatsappOrder.dataset.whatsappVariantLabel || ''}: ${variantLabel}` : '',
        `${whatsappOrder.dataset.whatsappQuantityLabel || 'Quantity'}: ${quantity}`,
        productUrl.toString(),
      ].filter(Boolean);
      whatsappOrder.href = `${whatsappOrder.dataset.whatsappBase}?text=${encodeURIComponent(messageParts.join(' — '))}`;
    };

    quantityInput?.addEventListener('input', updateWhatsAppOrder);
    quantityInput?.addEventListener('change', updateWhatsAppOrder);
    if (!select || !mainButton) {
      updateWhatsAppOrder();
      return;
    }

    const update = (updateUrl = true) => {
      const option = select.options[select.selectedIndex];
      const available = option.dataset.available === 'true';
      const currentPrice = option.dataset.price || '';
      const label = available ? mainButton.dataset.addLabel : mainButton.dataset.soldLabel;

      if (price) price.textContent = currentPrice;
      if (availability) {
        availability.textContent = available ? availability.dataset.availableLabel : availability.dataset.soldLabel;
        availability.classList.remove('product-availability--in-stock', 'product-availability--made-to-order', 'product-availability--sold-out');
        availability.classList.add(available ? (availability.dataset.availableClass || 'product-availability--in-stock') : 'product-availability--sold-out');
      }
      if (deliveryEstimate) {
        deliveryEstimate.textContent = available ? deliveryEstimate.dataset.availableLabel : deliveryEstimate.dataset.soldLabel;
      }
      mainButton.disabled = !available;
      mainButton.textContent = label;

      if (stickyButton) {
        stickyButton.disabled = !available;
        stickyButton.textContent = `${label} — ${currentPrice}`;
      }

      swatches.forEach((swatch) => {
        const isActive = swatch.dataset.variantId === option.value;
        swatch.classList.toggle('is-active', isActive);
        swatch.setAttribute('aria-pressed', String(isActive));
      });
      if (selectedVariantLabel) selectedVariantLabel.textContent = option.dataset.label || option.textContent.split('—')[0].trim();

      if (gallery && option.dataset.mediaId) {
        gallery.dispatchEvent(new CustomEvent('safioli:select-media', {
          detail: { mediaId: option.dataset.mediaId },
        }));
      }

      updateWhatsAppOrder();

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
      }
    };

    swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        select.value = swatch.dataset.variantId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    select.addEventListener('change', () => update());
    update(false);
  }

  bindMeasurements() {
    document.querySelectorAll('[data-product-measurements]').forEach((group) => {
      const values = [...group.querySelectorAll('[data-measure-value]')];
      const unitLabels = [...group.querySelectorAll('[data-measure-unit]')];
      const buttons = [...group.querySelectorAll('[data-measure-unit-button]')];
      if (!values.length || !buttons.length) return;

      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'fr', { maximumFractionDigits: 2 });
      const setUnit = (unit) => {
        values.forEach((value) => {
          const centimetres = Number(String(value.dataset.cm || '').replace(',', '.'));
          if (!Number.isFinite(centimetres)) return;
          value.textContent = formatter.format(unit === 'in' ? centimetres / 2.54 : centimetres);
        });
        unitLabels.forEach((label) => { label.textContent = unit === 'in' ? 'inch' : 'cm'; });
        buttons.forEach((button) => {
          const isActive = button.dataset.unit === unit;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', String(isActive));
        });
      };

      buttons.forEach((button) => button.addEventListener('click', () => setUnit(button.dataset.unit || 'cm')));
      setUnit('cm');
    });
  }

  bindStickyProductBar() {
    const stickyBar = document.querySelector('[data-sticky-product-bar]');
    const stickyButton = document.querySelector('[data-sticky-add]');
    const mainButton = document.querySelector('[data-main-add]');
    if (!stickyBar || !stickyButton || !mainButton) return;

    const setVisible = (visible) => {
      stickyBar.classList.toggle('is-visible', visible);
      stickyBar.setAttribute('aria-hidden', String(!visible));
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        setVisible(!entries[0].isIntersecting);
      }, { threshold: 0.15 });
      observer.observe(mainButton);
    } else {
      setVisible(true);
    }

    stickyButton.addEventListener('click', () => {
      if (!mainButton.disabled) mainButton.click();
    });
  }

  bindProductTabs() {
    document.querySelectorAll('[data-product-tabs]').forEach((group) => {
      const tabs = [...group.querySelectorAll('[data-product-tab]')];
      const panels = [...group.querySelectorAll('[data-product-panel]')];
      if (tabs.length < 2 || panels.length < 2) return;

      const activate = (activeTab, moveFocus = false) => {
        const targetId = activeTab.getAttribute('aria-controls');
        tabs.forEach((tab) => {
          const isActive = tab === activeTab;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', String(isActive));
          tab.tabIndex = isActive ? 0 : -1;
        });
        panels.forEach((panel) => {
          const isActive = panel.id === targetId;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });
        if (moveFocus) activeTab.focus();
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          let nextIndex = null;
          const isRtl = document.documentElement.dir === 'rtl';
          if (event.key === 'ArrowRight') nextIndex = isRtl ? (index - 1 + tabs.length) % tabs.length : (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = isRtl ? (index + 1) % tabs.length : (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (nextIndex === null) return;
          event.preventDefault();
          activate(tabs[nextIndex], true);
        });
      });
    });
  }

  bindProductDescriptionReadMore() {
    const mobileQuery = window.matchMedia('(max-width: 640px)');

    document.querySelectorAll('[data-product-description-clamp]').forEach((container) => {
      const content = container.querySelector('[data-product-description-content]');
      const toggle = container.querySelector('[data-product-description-toggle]');
      const label = toggle?.querySelector('[data-product-description-toggle-label]');
      if (!content || !toggle || !label) return;

      const setExpanded = (expanded) => {
        container.classList.toggle('is-expanded', expanded);
        toggle.setAttribute('aria-expanded', String(expanded));
        label.textContent = expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
        const icon = toggle.lastElementChild;
        if (icon) icon.textContent = expanded ? '↑' : '↓';
      };

      const sync = () => {
        container.classList.remove('is-collapsible', 'is-expanded');
        toggle.hidden = true;

        if (!mobileQuery.matches) return;

        window.requestAnimationFrame(() => {
          const needsToggle = content.scrollHeight > 288;
          container.classList.toggle('is-collapsible', needsToggle);
          toggle.hidden = !needsToggle;
          if (needsToggle) setExpanded(false);
        });
      };

      toggle.addEventListener('click', () => {
        setExpanded(toggle.getAttribute('aria-expanded') !== 'true');
      });
      mobileQuery.addEventListener?.('change', sync);
      window.addEventListener('load', sync, { once: true });
      sync();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new SafioliTheme());
